import React, { useState, useEffect, useRef } from 'react';
import { collection, addDoc, query, where, getDocs, serverTimestamp } from 'firebase/firestore';
import { db, auth, getCheckClientEmailExistsHTTPUrl } from '../../../firebase';
import { createSolicitudClienteWithAuth } from '../../auth/services/onboardingUserService';
import { useAuth } from '../../auth/components/AuthProvider';
import { useNavigate } from 'react-router-dom';
import { NotificationService } from '../../../utils/notificationService';
import { getRegiones, getComunas } from '../../../utils/regionesComunas';
import { signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import LogoKawesCar from '../../../components/LogoKawesCar';
import { LAUNCH } from '../../../constants/launchMessaging';
import { SHOW_PUBLIC_LOGIN_NAV } from '../../../constants/publicAccess';
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { formatRut, isValidRut, isValidTelefono } from '../../../utils/rutUtils';

const DEBUG_REGISTRO_FLASH = false; // true para logs de depuración del flujo submit → success

async function emailYaRegistrado(email) {
  const emailNorm = email.trim().toLowerCase();
  const url = getCheckClientEmailExistsHTTPUrl();
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email: emailNorm }),
  });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  return data.exists === true;
}

// Componente para registro sin autenticación
function RegistroSinAuth({
  onSubmissionStart,
  onSubmissionSuccess,
  onSubmissionError,
  isModal = false,
}) {
  const navigate = useNavigate();
  const { user, rol } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);

  // Función para obtener la ruta del dashboard según el rol
  const getDashboardRoute = () => {
    if (!user) return '/login';
    
    if (rol === 'admin') {
      return '/admin';
    } else if (rol === 'agente') {
      return '/dashboard/agente';
    } else if (rol === 'proveedor' || rol === 'empresa') {
      return '/dashboard/proveedor';
    } else if (rol === 'mecanico') {
      return '/dashboard/mecanico';
    } else if (rol === 'cliente') {
      return '/dashboard/cliente';
    } else {
      return '/dashboard/cliente';
    }
  };
  const [showStep0, setShowStep0] = useState(false);
  const [formData, setFormData] = useState({
    // Opciones paso 0: comunidad (siempre) y dashboard gratis (habilitar para avanzar)
    selectedOptions: { partOfCommunity: true, wantDashboard: true },
    // Información Personal
    nombres: '',
    apellidos: '',
    rut: '',
    telefono: '',
    email: '',
    password: '',
    confirmPassword: '',
    direccion: '',
    comuna: '',
    ciudad: '',
    region: '',
    // Información Adicional
    fecha_nacimiento: '',
    profesion: '',
    empresa: '',
    motivo_registro: '',
    servicios_interes: [],
    acepta_terminos: false,
    acepta_notificaciones: true
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [regionesDisponibles, setRegionesDisponibles] = useState([]);
  const [comunasDisponibles, setComunasDisponibles] = useState([]);
  const [verificandoEmail, setVerificandoEmail] = useState(false);
  const [emailExiste, setEmailExiste] = useState(false);
  const [usandoEmailGoogle, setUsandoEmailGoogle] = useState(false);
  const emailCheckTimeoutRef = useRef(null);
  const [mensajeEnvio, setMensajeEnvio] = useState({ tipo: null, texto: '' }); // 'success' | 'error' | null
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Pre-llenar nombre y apellido desde el perfil visitante si viene del flujo rápido
  useEffect(() => {
    try {
      const prefill = localStorage.getItem('kawescar_prefill_registro');
      console.log('prefill raw:', prefill);
      if (prefill) {
        const datos = JSON.parse(prefill);
        console.log('prefill parsed:', datos);
        setFormData(prev => ({
          ...prev,
          nombres: datos.nombres || prev.nombres,
          apellidos: datos.apellidos || prev.apellidos,
          email: datos.email || prev.email,
        }));
        localStorage.removeItem('kawescar_prefill_registro');
        return;
      }
      // Fallback: leer desde visitante
      const visitante = localStorage.getItem('kawescar_visitante');
      if (!visitante) return;
      const d = JSON.parse(visitante);
      if (d.nombre || d.apellido) {
        setFormData(prev => ({
          ...prev,
          nombres: d.nombre || prev.nombres,
          apellidos: d.apellido || prev.apellidos,
        }));
      }
    } catch (e) {}
  }, []);

  // Cargar regiones al montar el componente
  useEffect(() => {
    setRegionesDisponibles(getRegiones());
  }, []);

  // Si el usuario ya está autenticado, usar su email de Google
  useEffect(() => {
    if (user && user.email && !formData.email) {
      setFormData(prev => ({ ...prev, email: user.email }));
      setUsandoEmailGoogle(true);
      // Verificar si el email ya tiene cuenta
      if (user.email) {
        emailYaRegistrado(user.email)
          .then(existe => setEmailExiste(existe))
          .catch(() => setEmailExiste(false));
      }
    }
  }, [user]);

  // Actualizar comunas cuando cambie la región
  useEffect(() => {
    if (formData.region) {
      setComunasDisponibles(getComunas(formData.region));
      // Limpiar comuna si no está en la nueva región
      if (formData.comuna && !getComunas(formData.region).includes(formData.comuna)) {
        setFormData(prev => ({ ...prev, comuna: '' }));
      }
    } else {
      setComunasDisponibles([]);
    }
  }, [formData.region]);

  // Limpiar timeout al desmontar el componente
  useEffect(() => {
    return () => {
      if (emailCheckTimeoutRef.current) {
        clearTimeout(emailCheckTimeoutRef.current);
      }
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'servicios_interes') {
        setFormData(prev => ({
          ...prev,
          servicios_interes: checked 
            ? [...prev.servicios_interes, value]
            : prev.servicios_interes.filter(s => s !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else if (name === 'rut') {
      // Formatear RUT automáticamente mientras se escribe
      setFormData(prev => ({
        ...prev,
        [name]: formatRut(value)
      }));
    } else if (name === 'telefono') {
      // 9 dígitos (prefijo +56 fijo en UI; ej: 912345678)
      const v = value.replace(/\D/g, '').slice(0, 9);
      setFormData(prev => ({ ...prev, [name]: v }));
    } else if (name === 'fecha_nacimiento') {
      // Formatear fecha automáticamente: DD/MM/YYYY
      let val = value.replace(/[^\d]/g, '');
      
      // Limitar a 8 dígitos (DDMMYYYY)
      if (val.length > 8) val = val.slice(0, 8);
      
      // Formatear con barras automáticamente
      let formatted = val;
      if (val.length > 2) {
        formatted = val.slice(0, 2) + '/' + val.slice(2);
      }
      if (val.length > 4) {
        formatted = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.nombres.trim()) newErrors.nombres = 'Los nombres son obligatorios';
      if (!formData.apellidos.trim()) newErrors.apellidos = 'Los apellidos son obligatorios';
      if (!isModal) {
        if (!formData.rut.trim()) newErrors.rut = 'El RUT es obligatorio';
        else if (!isValidRut(formData.rut)) newErrors.rut = 'RUT inválido. Debe tener hasta 9 dígitos y terminar en número o K';
      }
      if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es obligatorio';
      else if (formData.telefono.length !== 9 || !/^\d{9}$/.test(formData.telefono)) newErrors.telefono = 'Ingresa los 9 dígitos de tu celular (ej: 912345678)';
      if (!formData.email.trim()) {
        newErrors.email = 'El email es obligatorio';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'El formato del email no es válido. Ejemplo: nombre@ejemplo.com';
        } else if (formData.email.trim().length > 254) {
          newErrors.email = 'El email es demasiado largo (máximo 254 caracteres)';
        } else if (emailExiste) {
          newErrors.email = 'Este email ya tiene una cuenta. Por favor, inicia sesión o usa otro email.';
        }
      }
      if (!formData.password) {
        newErrors.password = 'La contraseña es obligatoria';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    } else if (currentStep === 2) {
      if (!formData.direccion.trim()) newErrors.direccion = 'La dirección es obligatoria';
      if (!formData.region.trim()) newErrors.region = 'La región es obligatoria';
      if (!formData.comuna.trim()) newErrors.comuna = 'La comuna es obligatoria';
      if (!formData.acepta_terminos) newErrors.acepta_terminos = 'Debes aceptar los Términos y Condiciones para Clientes';
    } else if (currentStep === 3) {
      // paso 3: sin validaciones requeridas
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep()) {
      return;
    }

    if (DEBUG_REGISTRO_FLASH) console.log('[RegistroSinAuth] submit start');
    onSubmissionStart?.();
    setIsSubmitting(true);
    setMensajeEnvio({ tipo: null, texto: '' }); // Limpiar mensaje anterior

    try {
      // Verificar si el email ya existe (HTTP; si falla o 404, continuar sin bloquear)
      try {
        const origin = typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
        const url = origin ? `${origin}/api/checkClientEmailExistsHTTP` : 'https://us-central1-huamachuco-c0d9f.cloudfunctions.net/checkClientEmailExistsHTTP';
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email }),
        });
        if (res.ok) {
          const data = await res.json().catch(() => ({}));
          if (data && data.exists) {
            setErrors({ email: 'Ya existe una solicitud con este email' });
            setMensajeEnvio({
              tipo: 'error',
              texto: 'Ya existe una solicitud con este email. Por favor, usa otro email o contacta al administrador.',
            });
            setIsSubmitting(false);
            return;
          }
        }
        // 404 o error: no bloquear; el backend validará duplicados al crear
      } catch (checkErr) {
        console.warn('checkClientEmailExists no disponible o error:', checkErr);
      }

      // Verificar si ya tiene perfil básico (viene del flujo IA)
      try {
        const { db } = await import('../../../firebase');
        const { collection, query, where, getDocs } = await import('firebase/firestore');
        const q = query(
          collection(db, 'perfiles_basicos'),
          where('email', '==', formData.email.trim())
        );
        const snap = await getDocs(q);
        if (!snap.empty) {
          const perfilDoc = snap.docs[0];
          const uid = perfilDoc.data().uid;
          const { updateDoc, doc } = await import('firebase/firestore');
          await updateDoc(doc(db, 'usuarios', uid), {
            estadoInicial: false,
            pendienteActivacion: true,
            nombre: `${formData.nombres} ${formData.apellidos}`.trim(),
            telefono: formData.telefono,
            rut: formData.rut,
            direccion: formData.direccion,
            region: formData.region,
            comuna: formData.comuna,
            ciudad: formData.ciudad,
          });
          await updateDoc(doc(db, 'perfiles_basicos', uid), {
            apellido: formData.apellidos,
            telefono: formData.telefono,
            rut: formData.rut,
            perfilCompletoCompletado: true,
          });
          onSubmissionSuccess({ email: formData.email });
          return;
        }
      } catch (e) {
        console.warn('Error verificando perfil básico:', e);
      }

      // Onboarding con cuenta al enviar: crear Auth + usuarios + solicitudes_cliente → /pendiente
      const result = await createSolicitudClienteWithAuth(formData);
      if (!result.success) {
        if (DEBUG_REGISTRO_FLASH) console.log('[RegistroSinAuth] submit error', result.error || result.code);
        onSubmissionError?.();
        if (result.code === 'auth/email-already-in-use') {
          setErrors({ email: 'Este correo ya tiene una cuenta. Inicia sesión o restablece tu contraseña.' });
          setMensajeEnvio({ tipo: 'error', texto: 'Este correo ya está registrado. Ve a Iniciar sesión o usa "¿Olvidaste tu contraseña?".' });
        } else {
          setMensajeEnvio({ tipo: 'error', texto: result.error || 'Error al crear la cuenta. Intenta de nuevo.' });
        }
        setIsSubmitting(false);
        return;
      }
      addDoc(collection(db, 'terms_acceptances'), {
        type: 'clientes',
        version: 'KWC-TC-CLIENTES-v1.0',
        uid: result.uid,
        companyId: null,
        acceptedAt: serverTimestamp(),
        route: '/registro-cliente'
      }).catch(() => {});
      NotificationService.createInAppNotification(
        'admin',
        'validacion',
        'Nueva Solicitud de Cliente',
        `${formData.nombres} ${formData.apellidos} ha enviado una solicitud de registro`,
        { solicitudId: result.solicitudId, clienteNombre: `${formData.nombres} ${formData.apellidos}`, clienteEmail: formData.email, etapaActual: 'revision_inicial', origen: 'registro_cliente' }
      ).catch(() => {});
      if (DEBUG_REGISTRO_FLASH) console.log('[RegistroSinAuth] submit success');
      onSubmissionSuccess?.({ email: formData.email });
      return;
    } catch (error) {
      console.error('Error creating client request:', error);
      if (DEBUG_REGISTRO_FLASH) console.log('[RegistroSinAuth] submit exception', error.message);
      onSubmissionError?.();
      // Mostrar mensaje de error en el paso 3
      setMensajeEnvio({ 
        tipo: 'error', 
        texto: `Error al enviar la solicitud: ${error.message || 'Por favor, intenta nuevamente.'}` 
      });
      // Limpiar el mensaje después de 10 segundos
      setTimeout(() => {
        setMensajeEnvio({ tipo: null, texto: '' });
      }, 10000);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitting && currentStep === 2) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" aria-hidden />
        <p className="text-lg font-medium text-gray-800">Enviando tu solicitud...</p>
        <p className="text-sm text-gray-600 mt-1">Serás redirigido en un momento</p>
      </div>
    );
  }

  return (
    <form onSubmit={currentStep === 2 ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }}>
      {/* Resumen de opciones elegidas */}
      {!isModal && (
      <div className="mb-6 space-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1 flex items-center gap-2 rounded-xl border-2 border-blue-600 bg-blue-50 ring-2 ring-blue-200 px-4 py-3 text-left cursor-default">
            <span className="text-xl">👤</span>
            <span className="text-sm font-medium flex-1 text-blue-900">Ser parte de la comunidad KawesCar</span>
            <span className="text-blue-600 font-bold">✓</span>
          </div>
          <div className={`flex-1 flex items-center gap-2 rounded-xl border-2 px-4 py-3 text-left cursor-default ${formData.selectedOptions?.wantDashboard ? 'border-emerald-600 bg-emerald-50 ring-2 ring-emerald-200' : 'border-gray-200 bg-gray-50'}`}>
            <span className="text-xl">📊</span>
            <span className={`text-sm font-medium flex-1 ${formData.selectedOptions?.wantDashboard ? 'text-emerald-900' : 'text-gray-500'}`}>
              {LAUNCH.resumenSolicitudAccesoCliente}
            </span>
            {formData.selectedOptions?.wantDashboard && <span className="text-emerald-600 font-bold">✓</span>}
          </div>
        </div>
      </div>
      )}
      {currentStep === 1 && (
        <div className={isModal ? 'space-y-3' : 'space-y-4'}>
          <h2 className={isModal ? 'text-base font-bold text-gray-900' : 'text-2xl font-bold text-gray-900 mb-6'}>Información Personal</h2>
          <div className={`grid md:grid-cols-2 ${isModal ? 'gap-3' : 'gap-4'}`}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Nombres *</label>
              <input
                type="text"
                name="nombres"
                value={formData.nombres}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.nombres ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingresa tus nombres"
              />
              {errors.nombres && <p className="text-red-500 text-sm mt-1">{errors.nombres}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Apellidos *</label>
              <input
                type="text"
                name="apellidos"
                value={formData.apellidos}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.apellidos ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="Ingresa tus apellidos"
              />
              {errors.apellidos && <p className="text-red-500 text-sm mt-1">{errors.apellidos}</p>}
            </div>

            {!isModal && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">RUT *</label>
              <input
                type="text"
                name="rut"
                value={formData.rut}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  errors.rut ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="12.345.678-9"
                maxLength={12}
              />
              {errors.rut && <p className="text-red-500 text-sm mt-1">{errors.rut}</p>}
            </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
              <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`}>
                <span className="flex items-center px-4 bg-gray-100 text-gray-600 font-medium border-r border-gray-200">+56</span>
                <input
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={9}
                  name="telefono"
                  value={formData.telefono}
                  onChange={handleInputChange}
                  placeholder="912345678"
                  className="flex-1 px-4 py-3 border-0 bg-white text-gray-900 focus:ring-0 min-w-0"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">Ingresa los 9 dígitos (ej: 912345678)</p>
              {errors.telefono && <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>}
            </div>

            <div className={isModal ? '' : 'md:col-span-2'}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email *
                {usandoEmailGoogle && user && (
                  <span className="ml-2 text-xs text-green-600 font-normal">
                    (Usando tu cuenta de Google)
                  </span>
                )}
              </label>
              <div className="space-y-3">
                {/* Mensaje si está usando email de Google */}
                {usandoEmailGoogle && user && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                    <svg className="w-5 h-5 text-green-600 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-sm font-medium text-green-900">
                        Usando tu cuenta de Google: <span className="font-semibold">{user.email}</span>
                      </p>
                      <p className="text-xs text-green-700 mt-0.5">
                        Este email se usará para tu solicitud de acceso
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(prev => ({ ...prev, email: '' }));
                        setUsandoEmailGoogle(false);
                        setEmailExiste(false);
                      }}
                      className="text-xs text-green-700 hover:text-green-900 underline font-medium"
                    >
                      Cambiar
                    </button>
                  </div>
                )}
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  disabled={usandoEmailGoogle && user}
                  onChange={(e) => {
                    if (usandoEmailGoogle && user) {
                      // Si está usando email de Google, no permitir editar directamente
                      // Debe hacer clic en "Cambiar" primero
                      return;
                    }
                    handleInputChange(e);
                    const emailValue = e.target.value.trim();
                    
                    // Limpiar timeout anterior
                    if (emailCheckTimeoutRef.current) {
                      clearTimeout(emailCheckTimeoutRef.current);
                    }
                    
                    // Resetear estado si el email está vacío
                    if (!emailValue || !emailValue.includes('@')) {
                      setEmailExiste(false);
                      setVerificandoEmail(false);
                      return;
                    }
                    
                    // Validar formato básico de email
                    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                    if (!emailRegex.test(emailValue)) {
                      setEmailExiste(false);
                      setVerificandoEmail(false);
                      return;
                    }
                    
                    // Debounce: esperar 800ms después de que el usuario deje de escribir
                    setVerificandoEmail(true);
                    emailCheckTimeoutRef.current = setTimeout(async () => {
                      try {
                        const existe = await emailYaRegistrado(emailValue);
                        setEmailExiste(existe);
                      } catch (error) {
                        console.log('Error verificando email:', error.message);
                        setEmailExiste(false);
                      } finally {
                        setVerificandoEmail(false);
                      }
                    }, 800);
                  }}
                  className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                    errors.email ? 'border-red-500' : 
                    (usandoEmailGoogle && user) ? 'border-green-300 bg-green-50' : 'border-gray-300'
                  } ${(usandoEmailGoogle && user) ? 'cursor-not-allowed' : ''}`}
                  placeholder={usandoEmailGoogle && user ? user.email : "tu@email.com"}
                />
                {/* Indicador de verificación de email */}
                {verificandoEmail && (
                  <div className="flex items-center gap-2 text-sm text-gray-500 mt-1">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    <span>Verificando disponibilidad del email...</span>
                  </div>
                )}
                
                {/* Mensaje de email válido pero sin cuenta */}
                {!verificandoEmail && formData.email && !emailExiste && formData.email.includes('@') && 
                 /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email) && (
                  <div className="flex items-center gap-2 text-sm text-green-600 mt-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span>Email disponible ✓</span>
                  </div>
                )}

                {/* Mensaje de email existente con opción de login */}
                {emailExiste && formData.email && !verificandoEmail && (
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-300 rounded-lg p-4 mt-2">
                    <div className="flex items-start gap-3 mb-3">
                      <div className="flex-shrink-0">
                        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-blue-900 mb-1">
                          ¡Este email ya tiene una cuenta registrada!
                        </p>
                        <p className="text-xs text-blue-700 mb-3">
                          Si ya tienes una cuenta, puedes iniciar sesión directamente. Si olvidaste tu contraseña, puedes recuperarla desde el login.
                        </p>
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              const provider = new GoogleAuthProvider();
                              await signInWithPopup(auth, provider);
                              navigate('/dashboard/cliente');
                            } catch (error) {
                              console.error('Error en login con Google:', error);
                              if (error.code === 'auth/popup-closed-by-user') {
                                // Usuario cerró el popup, no mostrar error
                                return;
                              }
                              alert('Error al iniciar sesión. Por favor, intenta nuevamente o ve a la página de login.');
                            }
                          }}
                          className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white font-medium py-2.5 px-4 rounded-lg transition-all transform hover:scale-[1.02] shadow-md flex items-center justify-center gap-2"
                        >
                          <svg className="w-5 h-5" viewBox="0 0 24 24">
                            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                          Iniciar sesión con Google
                        </button>
                        {SHOW_PUBLIC_LOGIN_NAV && (
                          <div className="mt-2 flex items-center justify-center gap-2">
                            <span className="text-xs text-blue-600">o</span>
                            <a
                              href="/login"
                              className="text-xs text-blue-600 hover:text-blue-800 font-medium underline"
                            >
                              Ir a la página de login
                            </a>
                          </div>
                        )}
                        <p className="text-xs text-blue-600 mt-2 text-center italic">
                          Si quieres crear una cuenta nueva, usa un email diferente
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Mensajes de error mejorados */}
                {errors.email && (
                  <div className="mt-1 flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-2">
                    <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                    <p className="text-red-600 text-sm font-medium">{errors.email}</p>
                  </div>
                )}
              </div>
            </div>

            <div className={isModal ? 'contents' : 'space-y-4 max-w-md'}>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña *</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Mínimo 6 caracteres"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar contraseña *</label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                    placeholder="Repite tu contraseña"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(p => !p)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={showConfirmPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  >
                    {showConfirmPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className={isModal ? 'space-y-3' : 'space-y-6'}>
          {/* Bloque beneficios trial — visible en paso 2 antes de enviar */}
          <div className="rounded-2xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50 p-4">
            <p className="text-xs font-black uppercase tracking-widest text-indigo-500 mb-2">Al enviar tu solicitud obtienes</p>
            <div className="flex flex-col gap-2">
              <div className="flex items-start gap-2.5">
                <span className="text-base shrink-0">🎁</span>
                <div>
                  <p className="text-sm font-bold text-indigo-900">30 días gratis de Dashboard Básico</p>
                  <p className="text-xs text-indigo-600">Valor referencial <span className="font-bold">$4.990/mes</span> · Sin cargos automáticos.</p>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <span className="text-base shrink-0">⚡</span>
                <div>
                  <p className="text-sm font-bold text-amber-700">20% de descuento si contratas dentro de los 30 días</p>
                  <p className="text-xs text-amber-600">Aplica a cualquier plan — mensual o anual. Solo durante el período de prueba.</p>
                </div>
              </div>
            </div>
          </div>

          <h2 className={isModal ? 'text-base font-bold text-gray-900' : 'text-2xl font-bold text-gray-900 mb-6'}>
            Información de Residencia
          </h2>
          <div className={isModal ? 'space-y-3' : 'space-y-6'}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Dirección *
              </label>
              <input
                type="text"
                name="direccion"
                value={formData.direccion}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.direccion ? 'border-red-500' : 'border-gray-300'}`}
                placeholder="Calle, número, departamento"
              />
              {errors.direccion && <p className="text-red-500 text-sm mt-1">{errors.direccion}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Región *
              </label>
              <select
                name="region"
                value={formData.region}
                onChange={handleInputChange}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.region ? 'border-red-500' : 'border-gray-300'}`}
              >
                <option value="">Selecciona tu región</option>
                {regionesDisponibles.map(region => (
                  <option key={region} value={region}>{region}</option>
                ))}
              </select>
              {errors.region && <p className="text-red-500 text-sm mt-1">{errors.region}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comuna *
              </label>
              <select
                name="comuna"
                value={formData.comuna}
                onChange={handleInputChange}
                disabled={!formData.region}
                className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.comuna ? 'border-red-500' : 'border-gray-300'} ${!formData.region ? 'bg-gray-50' : ''}`}
              >
                <option value="">
                  {formData.region ? 'Selecciona tu comuna' : 'Selecciona primero una región'}
                </option>
                {comunasDisponibles.map(comuna => (
                  <option key={comuna} value={comuna}>{comuna}</option>
                ))}
              </select>
              {errors.comuna && <p className="text-red-500 text-sm mt-1">{errors.comuna}</p>}
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="acepta_terminos"
                  checked={formData.acepta_terminos}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  He leído y acepto los{' '}
                  <a href="/terminos/clientes" target="_blank" rel="noopener noreferrer"
                    className="text-blue-600 hover:underline">
                    Términos y Condiciones para Clientes
                  </a> *
                </label>
              </div>
              {errors.acepta_terminos && (
                <p className="text-red-500 text-sm">{errors.acepta_terminos}</p>
              )}
              <div className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  name="acepta_notificaciones"
                  checked={formData.acepta_notificaciones}
                  onChange={handleInputChange}
                  className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <label className="text-sm text-gray-700">
                  Acepto recibir notificaciones sobre recordatorios, ofertas y novedades
                </label>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Mensaje de éxito/error del envío (paso 2) */}
      {mensajeEnvio.tipo && currentStep === 2 && (
        <div className={`mt-4 p-4 rounded-lg border-2 ${mensajeEnvio.tipo === 'success' ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'}`}>
          <p className={`text-sm font-medium ${mensajeEnvio.tipo === 'success' ? 'text-green-900' : 'text-red-900'}`}>
            {mensajeEnvio.tipo === 'success' ? LAUNCH.exitoTitulo : '❌ Error al enviar solicitud'}
          </p>
          <p className={`text-sm mt-1 ${mensajeEnvio.tipo === 'success' ? 'text-green-800' : 'text-red-800'}`}>
            {mensajeEnvio.texto}
          </p>
        </div>
      )}

      {/* Navegación */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginTop: 20, gap: 8 }}>

        {/* Izquierda: Anterior */}
        <div>
          {currentStep > 1 && (
            <button
              type="button"
              onClick={handlePrevStep}
              style={{ backgroundColor: '#e5e7eb', color: '#374151', padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              ← Anterior
            </button>
          )}
        </div>

        {/* Centro: indicador de progreso */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[1, 2].map(step => (
              <div
                key={step}
                style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: step <= currentStep ? '#2563eb' : '#d1d5db' }}
              />
            ))}
          </div>
          <span style={{ fontSize: 11, color: '#6b7280', whiteSpace: 'nowrap' }}>Paso {currentStep} de 2</span>
        </div>

        {/* Derecha: Siguiente / Enviar */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          {currentStep < 2 ? (
            <button
              type="button"
              onClick={handleNextStep}
              style={{ backgroundColor: '#2563eb', color: 'white', padding: '9px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              Siguiente →
            </button>
          ) : (
            <button
              type="submit"
              disabled={isSubmitting || mensajeEnvio.tipo === 'success'}
              style={{ backgroundColor: isSubmitting || mensajeEnvio.tipo === 'success' ? '#6b7280' : '#059669', color: 'white', padding: '9px 16px', borderRadius: 8, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: 14, opacity: isSubmitting ? 0.6 : 1 }}
            >
              {isSubmitting ? 'Enviando…' : mensajeEnvio.tipo === 'success' ? '✓ Registrado' : 'Enviar solicitud'}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}

export default function RegistroCliente({ isModal = false, onClose } = {}) {
  const { user, rol } = useAuth();
  const navigate = useNavigate();
  const [perfilCliente, setPerfilCliente] = useState(null);
  const [loading, setLoading] = useState(true);
  // Máquina de estados para evitar flash del formulario tras enviar solicitud
  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [successEmail, setSuccessEmail] = useState('');
  const [successCountdown, setSuccessCountdown] = useState(3);

  // Redirección automática a home tras 3 s cuando status === 'success'
  useEffect(() => {
    if (status !== 'success') return;
    if (isModal) {
      setSuccessCountdown(3);
      const id = setInterval(() => {
        setSuccessCountdown(prev => {
          if (prev <= 1) { clearInterval(id); onClose?.(); return 0; }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(id);
    }
    setSuccessCountdown(3);
    const id = setInterval(() => {
      setSuccessCountdown(prev => {
        if (prev <= 1) {
          clearInterval(id);
          if (DEBUG_REGISTRO_FLASH) console.log('[RegistroCliente] redirect triggered by countdown');
          navigate('/', { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [status, navigate, isModal, onClose]);

  // Función para obtener la ruta del dashboard según el rol
  const getDashboardRoute = () => {
    if (!user) return '/login';
    
    if (rol === 'admin') {
      return '/admin';
    } else if (rol === 'agente') {
      return '/dashboard/agente';
    } else if (rol === 'proveedor' || rol === 'empresa') {
      return '/dashboard/proveedor';
    } else if (rol === 'mecanico') {
      return '/dashboard/mecanico';
    } else if (rol === 'cliente') {
      return '/dashboard/cliente';
    } else {
      return '/dashboard/cliente';
    }
  };
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Información Personal
    nombres: '',
    apellidos: '',
    rut: '',
    telefono: '',
    email: '',
    direccion: '',
    comuna: '',
    ciudad: '',
    region: '',
    
    // Información Adicional
    fecha_nacimiento: '',
    profesion: '',
    empresa: '',
    motivo_registro: '',
    servicios_interes: [],
    acepta_terminos: false,
    acepta_notificaciones: true,
    password: '',
    confirmPassword: '',
    plan_elegido: '',
    facturacion_tipo: '',
    facturacion_meses: 1,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [solicitudActual, setSolicitudActual] = useState(null);
  const [regionesDisponibles, setRegionesDisponibles] = useState([]);
  const [comunasDisponibles, setComunasDisponibles] = useState([]);
  const [showPwd, setShowPwd] = useState(false);
  const [showPwd2, setShowPwd2] = useState(false);

  useEffect(() => {
    try {
      const prefill = localStorage.getItem('kawescar_prefill_registro');
      if (prefill) {
        const datos = JSON.parse(prefill);
        setFormData(prev => ({
          ...prev,
          nombres: datos.nombres || prev.nombres,
          apellidos: datos.apellidos || prev.apellidos,
          email: datos.email || prev.email,
        }));
        localStorage.removeItem('kawescar_prefill_registro');
      }
    } catch (e) {}
  }, []);

  useEffect(() => { setRegionesDisponibles(getRegiones()); }, []);
  useEffect(() => {
    if (formData.region) {
      setComunasDisponibles(getComunas(formData.region));
      if (formData.comuna && !getComunas(formData.region).includes(formData.comuna)) {
        setFormData(prev => ({ ...prev, comuna: '' }));
      }
    } else {
      setComunasDisponibles([]);
    }
  }, [formData.region]);

  // Verificar si el usuario ya tiene un perfil de cliente o solicitud (no correr durante submit/success para evitar flash)
  useEffect(() => {
    if (status === 'submitting' || status === 'success') {
      if (DEBUG_REGISTRO_FLASH) console.log('[RegistroCliente] useEffect skip: status=', status);
      return;
    }
    const checkExistingProfile = async () => {
      if (!user) {
        setLoading(false);
        return;
      }

      try {
        // Verificar perfil existente
        const perfilQuery = query(
          collection(db, 'perfiles_clientes'),
          where('uid', '==', user.uid)
        );
        const perfilSnapshot = await getDocs(perfilQuery);
        
        if (!perfilSnapshot.empty) {
          const profile = perfilSnapshot.docs[0].data();
          setPerfilCliente(profile);
        } else {
          // Verificar solicitud existente
          const solicitudQuery = query(
            collection(db, 'solicitudes_cliente'),
            where('uid', '==', user.uid)
          );
          const solicitudSnapshot = await getDocs(solicitudQuery);
          
          if (!solicitudSnapshot.empty) {
            const solicitud = { 
              id: solicitudSnapshot.docs[0].id,
              ...solicitudSnapshot.docs[0].data() 
            };
            setSolicitudActual(solicitud);
          }
        }
      } catch (error) {
        console.error('Error checking existing profile:', error);
      } finally {
        setLoading(false);
      }
    };

    checkExistingProfile();
  }, [user, status]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'servicios_interes') {
        setFormData(prev => ({
          ...prev,
          servicios_interes: checked 
            ? [...prev.servicios_interes, value]
            : prev.servicios_interes.filter(s => s !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else if (name === 'rut') {
      // Formatear RUT automáticamente
      setFormData(prev => ({
        ...prev,
        [name]: formatRut(value)
      }));
    } else if (name === 'telefono') {
      // 9 dígitos (prefijo +56 fijo en UI; ej: 912345678)
      const v = value.replace(/\D/g, '').slice(0, 9);
      setFormData(prev => ({ ...prev, [name]: v }));
    } else if (name === 'fecha_nacimiento') {
      // Formatear fecha automáticamente: DD/MM/YYYY
      let val = value.replace(/[^\d]/g, '');
      
      // Limitar a 8 dígitos (DDMMYYYY)
      if (val.length > 8) val = val.slice(0, 8);
      
      // Formatear con barras automáticamente
      let formatted = val;
      if (val.length > 2) {
        formatted = val.slice(0, 2) + '/' + val.slice(2);
      }
      if (val.length > 4) {
        formatted = val.slice(0, 2) + '/' + val.slice(2, 4) + '/' + val.slice(4);
      }
      
      setFormData(prev => ({ ...prev, [name]: formatted }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Limpiar error cuando el usuario empiece a escribir
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (currentStep === 1) {
      if (!formData.nombres.trim()) newErrors.nombres = 'Los nombres son obligatorios';
      if (!formData.apellidos.trim()) newErrors.apellidos = 'Los apellidos son obligatorios';
      if (!formData.rut.trim()) newErrors.rut = 'El RUT es obligatorio';
      else if (!isValidRut(formData.rut)) newErrors.rut = 'RUT inválido. Debe tener hasta 9 dígitos y terminar en número o K';
      if (!formData.telefono.trim()) newErrors.telefono = 'El teléfono es obligatorio';
      else if (formData.telefono.length !== 9 || !/^\d{9}$/.test(formData.telefono)) newErrors.telefono = 'Ingresa los 9 dígitos de tu celular (ej: 912345678)';
      if (!formData.email.trim()) {
        newErrors.email = 'El email es obligatorio';
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email.trim())) {
          newErrors.email = 'El formato del email no es válido. Ejemplo: nombre@ejemplo.com';
        } else if (formData.email.trim().length > 254) {
          newErrors.email = 'El email es demasiado largo (máximo 254 caracteres)';
        }
      }
      if (!formData.password) {
        newErrors.password = 'La contraseña es obligatoria';
      } else if (formData.password.length < 6) {
        newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
      }
      if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Las contraseñas no coinciden';
      }
    } else if (currentStep === 2) {
      if (!formData.direccion.trim()) newErrors.direccion = 'La dirección es obligatoria';
      if (!formData.region.trim()) newErrors.region = 'La región es obligatoria';
      if (!formData.comuna.trim()) newErrors.comuna = 'La comuna es obligatoria';
      // Ciudad es opcional, no validar
    } else if (currentStep === 3) {
      if (!formData.plan_elegido) newErrors.plan_elegido = 'Selecciona el plan que quieres contratar';
      if (!formData.facturacion_tipo) newErrors.facturacion_tipo = 'Selecciona si pagarás mensual o anual';
      if (!formData.acepta_terminos) newErrors.acepta_terminos = 'Debes aceptar los Términos y Condiciones para Clientes';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNextStep = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateStep()) {
      return;
    }

    setIsSubmitting(true);
    setStatus('submitting');

    try {
      // Convertir fecha_nacimiento de DD/MM/YYYY a Date object
      let fechaNacimientoDate = null;
      if (formData.fecha_nacimiento) {
        const fechaRegex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
        const match = formData.fecha_nacimiento.match(fechaRegex);
        if (match) {
          const dia = parseInt(match[1], 10);
          const mes = parseInt(match[2], 10);
          const año = parseInt(match[3], 10);
          fechaNacimientoDate = new Date(año, mes - 1, dia);
        }
      }
      
      // Crear solicitud de cliente con estados y etapas (teléfono con prefijo +56)
      // eslint-disable-next-line no-unused-vars
      const { password, confirmPassword, ...formDataSinPassword } = formData;
      const solicitudData = {
        ...formDataSinPassword,
        telefono: formData.telefono.length === 9 ? '+56' + formData.telefono : formData.telefono,
        fecha_nacimiento: fechaNacimientoDate || formData.fecha_nacimiento, // Guardar como Date si es válida, sino mantener formato original
        uid: user.uid,
        email_usuario: user.email,
        fecha_solicitud: new Date(),
        
        // Estados de la solicitud
        estado_general: 'enviada', // enviada, en_revision, validacion_documentos, aprobada, rechazada
        etapa_actual: 'revision_inicial', // revision_inicial, validacion_datos, asignacion_agente, entrevista, aprobacion_final
        progreso_porcentaje: 10,
        
        // Seguimiento de etapas
        etapas: {
          revision_inicial: {
            estado: 'pendiente', // pendiente, en_proceso, completada, rechazada
            fecha_inicio: new Date(),
            fecha_fin: null,
            comentarios: '',
            responsable: null
          },
          validacion_datos: {
            estado: 'pendiente',
            fecha_inicio: null,
            fecha_fin: null,
            comentarios: '',
            responsable: null
          },
          asignacion_agente: {
            estado: 'pendiente',
            fecha_inicio: null,
            fecha_fin: null,
            comentarios: '',
            responsable: null
          },
          entrevista: {
            estado: 'pendiente',
            fecha_inicio: null,
            fecha_fin: null,
            comentarios: '',
            responsable: null
          },
          aprobacion_final: {
            estado: 'pendiente',
            fecha_inicio: null,
            fecha_fin: null,
            comentarios: '',
            responsable: null
          }
        },
        
        // Información adicional
        documentos_adjuntos: [],
        notas_admin: '',
        agente_asignado: null,
        fecha_entrevista: null,
        puntuacion_evaluacion: null,
        motivo_rechazo: '',
        fecha_aprobacion: null,
        
        // Metadatos
        ip_registro: window.location.hostname,
        user_agent: navigator.userAgent,
        referencia: 'web_directa',

        // Plan contratado
        plan_elegido: formData.plan_elegido,
        facturacion_tipo: formData.facturacion_tipo,
        facturacion_meses: formData.facturacion_tipo === 'anual' ? 12 : formData.facturacion_meses,

        // Aceptación T&C Clientes
        termsAcceptedClientes: true,
        termsAcceptedClientesAt: serverTimestamp(),
        termsVersionClientes: 'KWC-TC-CLIENTES-v1.0'
      };

      const docRef = await addDoc(collection(db, 'solicitudes_cliente'), solicitudData);

      await addDoc(collection(db, 'terms_acceptances'), {
        type: 'clientes',
        version: 'KWC-TC-CLIENTES-v1.0',
        uid: user?.uid || null,
        companyId: null,
        acceptedAt: serverTimestamp(),
        route: '/registro-cliente'
      });
      
      // Enviar notificación al admin
      await NotificationService.createInAppNotification(
        'admin',
        'validacion',
        'Nueva Solicitud de Cliente',
        `${formData.nombres} ${formData.apellidos} ha enviado una solicitud de registro`,
        {
          solicitudId: docRef.id,
          clienteNombre: `${formData.nombres} ${formData.apellidos}`,
          clienteEmail: formData.email,
          etapaActual: 'revision_inicial',
          origen: 'registro_cliente'
        }
      );

      localStorage.setItem('kawescar_solicitud_pendiente', '1');
      setSuccessEmail(formData.email || user?.email || '');
      setStatus('success');

    } catch (error) {
      console.error('Error creating client request:', error);
      setStatus('error');
      alert('Error al enviar la solicitud. Por favor, intenta nuevamente.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // 1) Submitting: solo pantalla de carga (nunca mostrar formulario)
  if (status === 'submitting') {
    if (DEBUG_REGISTRO_FLASH) console.log('[RegistroCliente] render status=submitting (loading)');
    if (isModal) {
      return (
        <div className="py-10 flex flex-col items-center justify-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mb-3" aria-hidden />
          <p className="text-sm font-medium text-gray-700">Enviando tu solicitud...</p>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" aria-hidden />
        <p className="text-lg font-medium text-gray-800">Enviando tu solicitud...</p>
        <p className="text-sm text-gray-600 mt-1">Un momento por favor</p>
      </div>
    );
  }

  // 2) Success: solo aviso de éxito + botón/countdown (nunca mostrar formulario)
  if (status === 'success') {
    if (DEBUG_REGISTRO_FLASH) console.log('[RegistroCliente] render status=success');
    if (isModal) {
      return (
        <div className="px-6 py-8 text-center">
          <div className="text-5xl mb-3">✅</div>
          <h2 className="text-xl font-bold text-green-800 mb-2">{LAUNCH.exitoTitulo}</h2>
          <p className="text-gray-600 text-sm mb-1">{LAUNCH.exitoClienteCuerpo}</p>
          {successEmail && (
            <p className="text-sm text-gray-500 mt-1">📧 <strong>{successEmail}</strong></p>
          )}
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-6">
        <div className="bg-white border border-green-200 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="text-5xl mb-4">✅</div>
          <h2 className="text-xl font-bold text-green-800 mb-3">{LAUNCH.exitoTitulo}</h2>
          <p className="text-gray-700 mb-4">{LAUNCH.exitoClienteCuerpo}</p>
          <p className="text-sm text-gray-600 mb-4">{LAUNCH.exitoClienteExtra}</p>
          {successEmail && (
            <p className="text-sm text-gray-600 mb-4">📧 Correo registrado: <strong>{successEmail}</strong></p>
          )}
          {successCountdown > 0 && (
            <p className="text-sm text-blue-600 mb-4">Volverás al inicio en {successCountdown} segundos...</p>
          )}
          <button
            type="button"
            onClick={() => {
              if (DEBUG_REGISTRO_FLASH) console.log('[RegistroCliente] redirect triggered by button');
              navigate('/', { replace: true });
            }}
            className="w-full bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors"
          >
            {LAUNCH.btnEntendido}
          </button>
        </div>
      </div>
    );
  }

  if (!user && isModal) {
    return (
      <div className="px-4 py-4">
        <RegistroSinAuth
          isModal={true}
          onSubmissionStart={() => setStatus('submitting')}
          onSubmissionSuccess={(data) => { localStorage.setItem('kawescar_solicitud_pendiente', '1'); setSuccessEmail(data?.email || ''); setStatus('success'); }}
          onSubmissionError={() => setStatus('error')}
        />
      </div>
    );
  }

  if (!user) {
    // Permitir registro sin autenticación previa
    // El usuario será creado durante el proceso
    return (
      <div className="min-h-screen bg-gray-50">
        {/* Header con Logo - Mismo que HomePage */}
        <header className="bg-white shadow-sm sticky top-0 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between py-4">
              <button
                onClick={() => navigate('/')}
                className="hover:opacity-80 transition-opacity"
              >
                <LogoKawesCar size="default" showText={true} />
              </button>
              <nav className="hidden md:flex items-center gap-6">
                <a
                  href="/area-cliente"
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Área Cliente
                </a>
                <a
                  href="/proveedores"
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Empresas
                </a>
                <a
                  href="/quienes-somos"
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Quiénes Somos
                </a>
                <a
                  href="/contacto"
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors"
                >
                  Contacto
                </a>
                {user ? (
                  <button
                    type="button"
                    onClick={() => navigate(getDashboardRoute())}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    {LAUNCH.ctaMiCuenta}
                  </button>
                ) : (
                  SHOW_PUBLIC_LOGIN_NAV && (
                    <button
                      type="button"
                      onClick={() => navigate('/login')}
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                    >
                      Iniciar Sesión
                    </button>
                  )
                )}
              </nav>
              {/* Menú móvil */}
              {SHOW_PUBLIC_LOGIN_NAV && (
                <button
                  type="button"
                  onClick={() => navigate('/login')}
                  className="md:hidden bg-blue-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Entrar
                </button>
              )}
            </div>
          </div>
        </header>

        <div className="py-8">
          <div className="max-w-2xl mx-auto px-4">
          
          <div className="bg-white rounded-lg shadow-md p-8">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">{LAUNCH.formClienteTitulo}</h1>
              <p className="text-gray-600">{LAUNCH.formClienteSubtitulo}</p>
            </div>

            <div className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 space-y-3">
              <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
                <strong>{LAUNCH.beneficioLanzamientoLabel}</strong> {LAUNCH.formClienteApoyo}
              </p>
              <p className="text-xs text-gray-600 text-center">
                {LAUNCH.pasoCeroClienteIntro}
              </p>
            </div>

            <RegistroSinAuth
              onSubmissionStart={() => setStatus('submitting')}
              onSubmissionSuccess={(data) => {
                localStorage.setItem('kawescar_solicitud_pendiente', '1');
                setSuccessEmail(data?.email || '');
                setStatus('success');
              }}
              onSubmissionError={() => setStatus('error')}
            />
          </div>
        </div>
      </div>
    </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-2xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">{LAUNCH.formClienteTitulo}</h1>
            <p className="text-gray-600">{LAUNCH.formClienteSubtitulo}</p>
          </div>

          <div className="mb-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-200 space-y-3">
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
              <strong>{LAUNCH.beneficioLanzamientoLabel}</strong> {LAUNCH.formClienteApoyo}
            </p>
            <p className="text-xs text-gray-600 text-center">
              {LAUNCH.pasoCeroClienteIntro}
            </p>
          </div>

          {perfilCliente ? (
            <div className="text-center space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
                <h2 className="text-xl font-semibold text-blue-800 mb-2">
                  ¡Tu perfil ya está registrado!
                </h2>
                <p className="text-blue-600 mb-4">
                  Estado: <span className="font-medium capitalize">{perfilCliente.estado_validacion}</span>
                </p>
                {perfilCliente.estado_validacion === 'pendiente' && (
                  <p className="text-sm text-blue-600 mb-4">
                    Tu perfil está siendo revisado por nuestro equipo. Te notificaremos cuando esté aprobado.
                  </p>
                )}
                {perfilCliente.estado_validacion === 'validado' && (
                  <p className="text-sm text-green-600 mb-4">
                    Tu perfil ha sido validado. Ya puedes iniciar sesión y acceder a tu cuenta de cliente.
                  </p>
                )}
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {LAUNCH.ctaMiCuenta}
                  </button>
                </div>
              </div>
            </div>
          ) : solicitudActual ? (
            <div className="min-h-[40vh] flex flex-col items-center justify-center p-6">
              <div className="bg-white border border-green-200 rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
                <div className="text-5xl mb-4">✅</div>
                <h2 className="text-xl font-bold text-green-800 mb-3">
                  {LAUNCH.exitoTitulo}
                </h2>
                <p className="text-gray-700 mb-4">
                  {LAUNCH.exitoClienteCuerpo}
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  {LAUNCH.exitoClienteExtra}
                </p>
                <p className="text-sm text-gray-600 mb-6">
                  📧 Correo registrado: <strong>{solicitudActual?.email_contacto || formData?.email || ''}</strong>
                </p>
                <button
                  type="button"
                  onClick={() => navigate('/', { replace: true })}
                  className="w-full bg-green-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-green-700 transition-colors"
                >
                  {LAUNCH.btnEntendido}
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={currentStep === 3 ? handleSubmit : (e) => { e.preventDefault(); handleNextStep(); }}>
              {currentStep === 1 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Información Personal</h2>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Nombres *</label>
                      <input
                        type="text"
                        name="nombres"
                        value={formData.nombres}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.nombres ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ingresa tus nombres"
                      />
                      {errors.nombres && <p className="text-red-500 text-sm mt-1">{errors.nombres}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Apellidos *</label>
                      <input
                        type="text"
                        name="apellidos"
                        value={formData.apellidos}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.apellidos ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Ingresa tus apellidos"
                      />
                      {errors.apellidos && <p className="text-red-500 text-sm mt-1">{errors.apellidos}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">RUT *</label>
                      <input
                        type="text"
                        name="rut"
                        value={formData.rut}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.rut ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="12.345.678-9"
                      />
                      {errors.rut && <p className="text-red-500 text-sm mt-1">{errors.rut}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono *</label>
                      <div className={`flex border rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`}>
                        <span className="flex items-center px-4 bg-gray-100 text-gray-600 font-medium border-r border-gray-200">+56</span>
                        <input
                          type="tel"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          maxLength={9}
                          name="telefono"
                          value={formData.telefono}
                          onChange={handleInputChange}
                          placeholder="912345678"
                          className="flex-1 px-4 py-3 border-0 bg-white text-gray-900 focus:ring-0 min-w-0"
                        />
                      </div>
                      <p className="text-xs text-gray-500 mt-1">Ingresa los 9 dígitos de tu celular (ej: 912345678)</p>
                      {errors.telefono && <p className="text-red-500 text-sm mt-1">{errors.telefono}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="tu@email.com"
                      />
                      {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña *</label>
                      <div className="relative">
                        <input
                          type={showPwd ? 'text' : 'password'}
                          name="password"
                          value={formData.password || ''}
                          onChange={handleInputChange}
                          autoComplete="new-password"
                          className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="Mínimo 6 caracteres"
                        />
                        <button type="button" onClick={() => setShowPwd(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPwd ? '🙈' : '👁️'}
                        </button>
                      </div>
                      {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar contraseña *</label>
                      <div className="relative">
                        <input
                          type={showPwd2 ? 'text' : 'password'}
                          name="confirmPassword"
                          value={formData.confirmPassword || ''}
                          onChange={handleInputChange}
                          autoComplete="new-password"
                          className={`w-full px-4 py-3 pr-12 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
                          placeholder="Repite tu contraseña"
                        />
                        <button type="button" onClick={() => setShowPwd2(p => !p)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
                          {showPwd2 ? '🙈' : '👁️'}
                        </button>
                      </div>
                      {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
                    </div>
                  </div>
                </div>
              )}

              {currentStep === 2 && (
                <div className="space-y-6">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Información de Residencia</h2>
                  <div className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Dirección *</label>
                      <input
                        type="text"
                        name="direccion"
                        value={formData.direccion}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.direccion ? 'border-red-500' : 'border-gray-300'}`}
                        placeholder="Calle, número, departamento"
                      />
                      {errors.direccion && <p className="text-red-500 text-sm mt-1">{errors.direccion}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Región *</label>
                      <select
                        name="region"
                        value={formData.region}
                        onChange={handleInputChange}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.region ? 'border-red-500' : 'border-gray-300'}`}
                      >
                        <option value="">Selecciona tu región</option>
                        {regionesDisponibles.map(region => (
                          <option key={region} value={region}>{region}</option>
                        ))}
                      </select>
                      {errors.region && <p className="text-red-500 text-sm mt-1">{errors.region}</p>}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Comuna *</label>
                      <select
                        name="comuna"
                        value={formData.comuna}
                        onChange={handleInputChange}
                        disabled={!formData.region}
                        className={`w-full px-4 py-3 border rounded-lg bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.comuna ? 'border-red-500' : 'border-gray-300'} ${!formData.region ? 'bg-gray-50' : ''}`}
                      >
                        <option value="">
                          {formData.region ? 'Selecciona tu comuna' : 'Selecciona primero una región'}
                        </option>
                        {comunasDisponibles.map(comuna => (
                          <option key={comuna} value={comuna}>{comuna}</option>
                        ))}
                      </select>
                      {errors.comuna && <p className="text-red-500 text-sm mt-1">{errors.comuna}</p>}
                    </div>

                  </div>
                </div>
              )}

              {currentStep === 3 && (() => {
                const PLANES = [
                  {
                    id: 'basic',
                    label: 'Dashboard Base',
                    precio: 4990,
                    precioAnual: 49990,
                    color: 'blue',
                    features: ['1 vehículo incluido', 'Copiloto IA · 30 int/mes', 'Gastos, documentos, peajes y más'],
                  },
                  {
                    id: 'premium',
                    label: 'Premium Conductor',
                    precio: 12990,
                    precioAnual: 119990,
                    badge: 'Más popular',
                    color: 'indigo',
                    features: ['3 vehículos incluidos', 'Copiloto IA · 100 int/mes', 'Consulta patentes · TAG · Multas'],
                  },
                  {
                    id: 'premium_vehicular',
                    label: 'Premium Vehicular',
                    precio: 24990,
                    precioAnual: 239990,
                    color: 'violet',
                    features: ['10 vehículos incluidos', 'Copiloto IA · 500 int/mes', 'Consulta patentes · 100/mes'],
                  },
                ];
                const planActual = PLANES.find(p => p.id === formData.plan_elegido);
                const totalMensual = planActual && formData.facturacion_tipo === 'mensual'
                  ? planActual.precio * formData.facturacion_meses
                  : null;
                const colorMap = { blue: { ring: 'ring-blue-500 bg-blue-50', badge: 'bg-blue-100 text-blue-700', dot: 'bg-blue-500' }, indigo: { ring: 'ring-indigo-500 bg-indigo-50', badge: 'bg-indigo-100 text-indigo-700', dot: 'bg-indigo-500' }, violet: { ring: 'ring-violet-500 bg-violet-50', badge: 'bg-violet-100 text-violet-700', dot: 'bg-violet-500' } };

                return (
                  <div className="space-y-6">
                    <h2 className="text-2xl font-bold text-gray-900">Elige tu plan</h2>

                    {/* ── Selección de plan ── */}
                    <div className="grid grid-cols-1 gap-3">
                      {PLANES.map(plan => {
                        const selected = formData.plan_elegido === plan.id;
                        const c = colorMap[plan.color];
                        return (
                          <button
                            key={plan.id}
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, plan_elegido: plan.id }))}
                            className={`w-full text-left rounded-xl border-2 px-4 py-3 transition-all ${selected ? `ring-2 ${c.ring} border-transparent` : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                          >
                            <div className="flex items-start justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <span className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 flex items-center justify-center ${selected ? `border-${plan.color}-500` : 'border-gray-300'}`}>
                                  {selected && <span className={`h-2 w-2 rounded-full ${c.dot}`} />}
                                </span>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="font-bold text-gray-900 text-sm">{plan.label}</span>
                                    {plan.badge && <span className={`text-[10px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${c.badge}`}>{plan.badge}</span>}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                                    {plan.features.map((f, i) => (
                                      <span key={i} className="text-[11px] text-gray-500">{f}</span>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-sm font-black text-gray-900">${plan.precio.toLocaleString('es-CL')}<span className="text-xs font-normal text-gray-500">/mes</span></p>
                                <p className="text-[10px] text-gray-400">Anual ${plan.precioAnual.toLocaleString('es-CL')}</p>
                              </div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {errors.plan_elegido && <p className="text-red-500 text-sm">{errors.plan_elegido}</p>}

                    {/* ── Tipo de facturación ── */}
                    {formData.plan_elegido && (
                      <div className="space-y-3">
                        <p className="text-sm font-semibold text-gray-700">Tipo de facturación</p>
                        <div className="grid grid-cols-2 gap-3">
                          {['mensual', 'anual'].map(tipo => {
                            const selected = formData.facturacion_tipo === tipo;
                            return (
                              <button
                                key={tipo}
                                type="button"
                                onClick={() => setFormData(prev => ({ ...prev, facturacion_tipo: tipo, facturacion_meses: tipo === 'anual' ? 12 : 1 }))}
                                className={`rounded-xl border-2 px-4 py-3 text-left transition-all ${selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300 bg-white'}`}
                              >
                                <p className={`font-bold text-sm capitalize ${selected ? 'text-blue-700' : 'text-gray-800'}`}>{tipo === 'anual' ? 'Anual' : 'Mensual'}</p>
                                {tipo === 'anual' && planActual && (
                                  <p className="text-[11px] text-gray-500 mt-0.5">${planActual.precioAnual.toLocaleString('es-CL')} · 2 meses gratis</p>
                                )}
                                {tipo === 'mensual' && <p className="text-[11px] text-gray-500 mt-0.5">Elige cuántos meses</p>}
                              </button>
                            );
                          })}
                        </div>
                        {errors.facturacion_tipo && <p className="text-red-500 text-sm">{errors.facturacion_tipo}</p>}

                        {/* ── Cantidad de meses (solo si mensual) ── */}
                        {formData.facturacion_tipo === 'mensual' && (
                          <div className="space-y-2">
                            <p className="text-sm font-semibold text-gray-700">¿Cuántos meses?</p>
                            <div className="grid grid-cols-4 gap-2">
                              {[1, 3, 6, 12].map(m => {
                                const sel = formData.facturacion_meses === m;
                                return (
                                  <button
                                    key={m}
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, facturacion_meses: m }))}
                                    className={`rounded-lg border-2 py-2 text-sm font-bold transition-all ${sel ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 hover:border-gray-300 text-gray-700'}`}
                                  >
                                    {m === 1 ? '1 mes' : `${m} meses`}
                                  </button>
                                );
                              })}
                            </div>
                            {planActual && (
                              <p className="text-xs text-gray-500 pt-1">
                                Total estimado: <span className="font-bold text-gray-800">${(planActual.precio * formData.facturacion_meses).toLocaleString('es-CL')}</span>
                              </p>
                            )}
                          </div>
                        )}

                        {/* ── Resumen del plan elegido ── */}
                        {formData.facturacion_tipo && planActual && (
                          <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                            <p className="text-xs font-black uppercase tracking-wide text-gray-500 mb-1">Resumen</p>
                            <p className="text-sm font-bold text-gray-900">{planActual.label} · {formData.facturacion_tipo === 'anual' ? 'Anual' : `${formData.facturacion_meses} ${formData.facturacion_meses === 1 ? 'mes' : 'meses'}`}</p>
                            <p className="text-sm text-gray-600">
                              Total: <span className="font-black text-gray-900">
                                ${(formData.facturacion_tipo === 'anual' ? planActual.precioAnual : planActual.precio * formData.facturacion_meses).toLocaleString('es-CL')}
                              </span>
                              {formData.facturacion_tipo === 'mensual' && formData.facturacion_meses > 1 && (
                                <span className="text-gray-400 text-xs ml-1">({formData.facturacion_meses} × ${planActual.precio.toLocaleString('es-CL')})</span>
                              )}
                            </p>
                          </div>
                        )}
                      </div>
                    )}

                    {/* ── Términos y condiciones ── */}
                    <div className="space-y-4 pt-2 border-t border-gray-100">
                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="acepta_terminos"
                          checked={formData.acepta_terminos}
                          onChange={handleInputChange}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-sm text-gray-700">
                          He leído y acepto los{' '}
                          <a href="/terminos/clientes" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                            Términos y Condiciones para Clientes
                          </a> *
                        </label>
                      </div>
                      {errors.acepta_terminos && <p className="text-red-500 text-sm">{errors.acepta_terminos}</p>}

                      <div className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          name="acepta_notificaciones"
                          checked={formData.acepta_notificaciones}
                          onChange={handleInputChange}
                          className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <label className="text-sm text-gray-700">
                          Acepto recibir notificaciones sobre recordatorios, ofertas y novedades de la plataforma
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })()}


              {/* Navegación */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', marginTop: 28, gap: 8 }}>

                {/* Izquierda: Anterior */}
                <div>
                  {currentStep > 1 && (
                    <button
                      type="button"
                      onClick={handlePrevStep}
                      className="bg-gray-300 text-gray-700 px-5 py-2.5 rounded-lg hover:bg-gray-400 transition-colors font-medium"
                    >
                      ← Anterior
                    </button>
                  )}
                </div>

                {/* Centro: indicador de progreso */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[1, 2, 3].map(step => (
                      <div
                        key={step}
                        style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: step <= currentStep ? '#2563eb' : '#d1d5db' }}
                      />
                    ))}
                  </div>
                  <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>Paso {currentStep} de 3</span>
                </div>

                {/* Derecha: Siguiente / Enviar */}
                <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  {currentStep < 3 ? (
                    <button
                      type="button"
                      onClick={handleNextStep}
                      className="bg-blue-600 text-white px-5 py-2.5 rounded-lg hover:bg-blue-700 transition-colors font-medium"
                    >
                      Siguiente →
                    </button>
                  ) : (
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="bg-green-600 text-white px-5 py-2.5 rounded-lg hover:bg-green-700 transition-colors font-medium disabled:opacity-50"
                    >
                      {isSubmitting ? 'Enviando…' : 'Enviar solicitud'}
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
